import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTenantQuery } from 'probonio-shared-ui/module/api';
import { useMeStateSelector } from 'probonio-shared-ui/module/me/types';
import { useCallback } from 'react';
import { BikeleasingContractDocumentType, CONTRACT_DOCUMENTS } from '../model/contract-documents';

const pdfAvailabilityQueryKey = (contractId: string, documentType: BikeleasingContractDocumentType) =>
  ['benefit', 'bikeleasing', 'contracts', contractId, 'pdf', documentType] as const;

export function useContractPdfAvailable(contractId: string, documentType: BikeleasingContractDocumentType) {
  const tenantId = useMeStateSelector(state => state.me.currentTenantId);
  const queryClient = useQueryClient();

  const { data: pdfAvailable, isPending } = useTenantQuery(
    pdfAvailabilityQueryKey(contractId, documentType),
    async tenantId => {
      try {
        await CONTRACT_DOCUMENTS[documentType].headFile({ tenantId, contractId });
        return true;
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
          return false;
        }
        throw err;
      }
    },
    { retry: false },
  );

  const markUnavailable = useCallback(() => {
    if (!tenantId) {
      return;
    }
    queryClient.setQueryData(['tenants', tenantId, ...pdfAvailabilityQueryKey(contractId, documentType)], false);
  }, [contractId, documentType, queryClient, tenantId]);

  return { pdfAvailable, isCheckingAvailability: isPending, markUnavailable };
}
